/**
 * 008-reporting-dashboards T019 (FR-012, D-29). Unlike the other three proof reports, this one
 * takes no from/to — `"/api/warehouse/reports/collisions"` accepts no query params at all (verified
 * against `warehouse.d.ts`'s `query?: never` and the backend's own contract: a static snapshot of
 * the catalogue's tolerance-band overlaps "as they stand", `dateField: "none"`). So no period form,
 * unlike `StockMovement.tsx`/`Variance.tsx` — it loads on mount instead.
 *
 * Gated on `packing.oversight` (008-reporting-dashboards T037, contracts/screen-routes.md) — was
 * `inventory.read` (the nearest existing precedent before T037 added a dedicated code) — the backend's
 * real permission for this route is `packing.oversight`, no equivalent code exists here either way
 * (GAP-5), cosmetic only, the backend enforces its own code regardless.
 *
 * Both sections render through `ReportTable`, not `PagedTable` — the whole snapshot arrives in one
 * response, and this screen exports (`ExportButton`); `export-guard.test.ts` forbids pairing a
 * server-paged table with the unpaged CSV button, and its detector reads the import name, not
 * whether a given `PagedTable` instance is actually server-paged. `ReportTable` pages the same rows
 * in memory, same reason `Variance.tsx`/`StockMovement.tsx` use it.
 */
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Alert } from "rizzui";
import { WidgetCard, ErrorSurface, EmptyState, ExportButton, Skeleton } from "@steelyard/theme";
import { ListHeader } from "../../components/table/list-header";
import { ReportTable } from "../../components/table/table-view";
import { can } from "../../lib/permissions";
import { formatMoment, sentenceCase } from "../../lib/format";
import { getCollisionReport, formatQty, type CollisionReportView, type CollisionPair, type CollisionCandidate } from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";
import { collisionPairColumns, collisionExcludedColumns } from "./collisions-columns";

export default function CollisionsReportPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [report, setReport] = useState<CollisionReportView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  // No params to drive from the URL — this report has no range. Load once, on mount.
  useEffect(() => {
    run();
  }, []);

  if (!can(permissionState, "packing.oversight")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403, detail: "The weight-collision report is gated by packing.oversight, which your role does not hold." }} action="view the weight-collision report" />;
  }

  async function run() {
    setLoading(true);
    setError(null);
    try {
      setReport(await getCollisionReport());
    } catch (err) {
      setError(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  const header = (
    <ListHeader
      title="Weight-Collision Report"
      count={report && !report.empty ? report.pairs.length : null}
      description="SKU pairs whose expected-weight tolerance bands overlap closely enough that a scale reading alone cannot tell them apart — packing either one forces an explicit confirmation."
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Inventory" }, { name: "Weight-Collision Report" }]}
    >
      {report && !report.empty ? (
        <ExportButton
          // The overlapping pairs only (the report's own finding) — mapped in the EXACT displayed
          // column order, not passed as CollisionPair[] directly (flattenObject's for...in
          // key-order hazard, see GstSummary.tsx's own comment).
          data={report.pairs.map((r) => ({
            "SKU A": r.skuAName,
            "Expected weight A (kg)": formatQty(r.skuAExpectedWeight),
            "SKU B": r.skuBName,
            "Expected weight B (kg)": formatQty(r.skuBExpectedWeight),
            "Overlap (kg)": formatQty(r.overlapKg),
          }))}
          header="SKU A,Expected weight A (kg),SKU B,Expected weight B (kg),Overlap (kg)"
          fileName={`collisions-${report.producedAt.slice(0, 10)}`}
        />
      ) : null}
    </ListHeader>
  );

  if (loading && !report && !error) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <WidgetCard title="Loading the snapshot…" rounded="lg" headerClassName="border-muted border-b pb-4">
          <div aria-hidden className="flex flex-col gap-3 pt-4">
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </WidgetCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}

      {error ? <ErrorSurface error={error} action="load the weight-collision report" /> : null}

      {report ? (
        report.empty ? (
          <WidgetCard title={<>Snapshot at {formatMoment(report.producedAt)}</>} rounded="lg" headerClassName="border-muted border-b">
            <EmptyState title="No colliding SKU pairs">
              {report.emptyReason ? sentenceCase(report.emptyReason) + "." : "No two SKUs in the catalogue currently overlap on expected weight."}
            </EmptyState>
          </WidgetCard>
        ) : (
          <>
            <Alert color="danger" bar className="mb-2">
              {report.forcedConfirmationStatement}
            </Alert>

            {report.pairs.length > 0 ? (
              <ReportTable<CollisionPair>
                key={`${report.producedAt}-pairs`}
                rows={report.pairs}
                columns={collisionPairColumns}
                caption="Overlapping SKU pairs, with the overlap extent"
                title="Overlapping pairs"
                description={<>Snapshot at {formatMoment(report.producedAt)}</>}
                searchPlaceholder="Search SKUs…"
              />
            ) : (
              <WidgetCard title="Overlapping pairs" rounded="lg" headerClassName="border-muted border-b">
                <EmptyState title="No colliding SKU pairs">Only SKUs without an expected weight were found — listed below.</EmptyState>
              </WidgetCard>
            )}

            {report.excluded.length > 0 ? (
              <ReportTable<CollisionCandidate>
                key={`${report.producedAt}-excluded`}
                rows={report.excluded}
                columns={collisionExcludedColumns}
                caption="SKUs excluded from the collision check for want of an expected weight"
                title={<>Could not be checked · {report.excluded.length}</>}
                description="These SKUs have no expected weight to compare — listed, not treated as collision-free."
              />
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
}
